import { Archive, ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SectionHeader } from './SectionHeader';
import { ActivityItem } from '@/types';
import { formatDateByDayjs } from '@/utils';

interface ActivityLogSectionProps {
  items: ActivityItem[];
  isLoading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** The order is older than the hot window, so its history lives in cold storage. */
  archived?: boolean;
  /** The merchant has asked for that history — until then nothing is fetched. */
  requested?: boolean;
  onRequest?: () => void;
}

export function ActivityLogSection({
  items,
  isLoading,
  hasMore,
  loadingMore,
  onLoadMore,
  archived = false,
  requested = false,
  onRequest,
}: ActivityLogSectionProps) {
  const { t } = useTranslation();

  // Before the loading branch: an archived order has not been fetched yet, so
  // `isLoading` would flash a spinner for a request that is not happening.
  if (archived && !requested) {
    return (
      <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
        <SectionHeader icon={ClipboardList} title={String(t('activity_log'))} />

        <div className='flex flex-col items-center gap-2 py-6 text-center'>
          <Archive className='h-6 w-6 text-sd-muted-foreground/60' aria-hidden='true' />
          <p className='text-[13px] font-semibold text-sd-foreground m-0'>
            {String(t('activity_log_archived_title'))}
          </p>
          <p className='text-[12px] text-sd-muted-foreground m-0 max-w-[42ch] leading-snug'>
            {String(t('activity_log_archived_hint'))}
          </p>
          <button
            type='button'
            onClick={onRequest}
            className='mt-1 text-[12px] font-medium text-sd-primary hover:text-sd-primary/80 cursor-pointer bg-transparent border-0 p-0'
          >
            {String(t('activity_log_archived_action'))}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
        <SectionHeader icon={ClipboardList} title={String(t('activity_log'))} />
        <div className='flex items-center justify-center py-6'>
          <div className='h-5 w-5 animate-spin rounded-full border-2 border-sd-primary border-t-transparent' />
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) return null;

  // F-8: Ensure chronological sort (newest first) — API may return inconsistent order
  const sortedItems = [...items].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
      <SectionHeader icon={ClipboardList} title={String(t('activity_log'))} />

      <div className='space-y-0'>
        {sortedItems.map((item, index) => (
          <div key={`${item.created_at}-${index}`} className='relative py-3 first:pt-0 last:pb-0'>
            {/* Connector line */}
            {index < sortedItems.length - 1 && (
              <div className='absolute start-[7px] top-[20px] w-0.5 h-full bg-sd-border' />
            )}

            <div className='flex items-start gap-3'>
              {/* Dot */}
              <div className='shrink-0 mt-1.5 h-[14px] w-[14px] rounded-full bg-sd-primary/20 border-2 border-sd-primary z-10' />

              {/* Content */}
              <div className='flex-1 min-w-0'>
                <p className='text-[13px] font-semibold text-sd-foreground m-0 leading-tight'>
                  {item.title}
                </p>
                {item.description && (
                  <p className='text-[12px] text-sd-muted-foreground mt-0.5 m-0 leading-snug'>
                    {item.description}
                  </p>
                )}
                <p className='text-[11px] text-sd-muted-foreground/70 mt-1 m-0'>
                  {formatDateByDayjs(item.created_at, 'DD MMM YYYY, HH:mm')}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          type='button'
          onClick={onLoadMore}
          disabled={loadingMore}
          className='mt-3 w-full text-center text-[12px] font-medium text-sd-primary hover:text-sd-primary/80 cursor-pointer disabled:opacity-50 bg-transparent border-0 p-0'
        >
          {loadingMore ? String(t('loading')) : String(t('load_more'))}
        </button>
      )}
    </div>
  );
}
